import { X, Check } from "lucide-react";

const FREE_FEATURES = [
  "Up to 3 accounts",
  "Net worth dashboard",
  "Allocation breakdown",
  "Manual balance updates",
];

const YEARLY_FEATURES = [
  "Auto-sync net worth daily",
  "Connect up to 10 accounts",
  "Full allocation & history charts",
  "Email support",
];

const LIFETIME_FEATURES = [
  "Auto-sync net worth in real time",
  "Unlimited connected accounts",
  "Multi-currency with live FX rates",
  "Travel-points valuation engine",
  "Priority support & early features",
  "All future updates included, free",
];

function Feature({ children, stroke, className = "" }) {
  return (
    <div className={`flex items-start gap-[11px] text-sm ${className}`}>
      <Check
        className="mt-px h-[17px] w-[17px] flex-none"
        strokeWidth={2.2}
        style={{ color: stroke }}
      />
      {children}
    </div>
  );
}

export default function PricingModal({ open, onClose }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[110] flex flex-col bg-vaulzo-bg animate-vz-fade">
      <button
        onClick={onClose}
        title="Close"
        aria-label="Close"
        className="fixed right-[26px] top-[22px] flex h-[42px] w-[42px] items-center justify-center rounded-full bg-vaulzo-close-red text-white transition-opacity hover:opacity-[0.55]"
      >
        <X className="h-[19px] w-[19px]" strokeWidth={2.4} />
      </button>

      <div className="flex flex-1 justify-center overflow-y-auto px-6 pb-16 pt-[88px]">
        <div className="w-full max-w-[1020px]">
          <div className="mb-[52px] text-center">
            <div className="mb-[14px] text-[12px] uppercase tracking-[0.14em] text-vaulzo-fainter">
              Vaulzo Premium
            </div>
            <h2 className="m-0 mb-3 text-[34px] font-bold tracking-[-0.03em] text-vaulzo-ink">
              Auto-sync your entire net worth
            </h2>
            <p className="mx-auto max-w-[460px] text-[15px] leading-relaxed text-vaulzo-muted">
              Connect your banks, brokers and wallets once — Vaulzo keeps every balance current, on its own.
            </p>
          </div>

          <div className="grid items-stretch gap-4 md:grid-cols-[1fr_1fr_1.06fr]">
            {/* Free */}
            <div className="flex flex-col rounded-[20px] border border-[#E7E9EE] bg-white px-[26px] py-[30px]">
              <div className="text-[13px] font-bold uppercase tracking-[0.06em] text-vaulzo-faint">Free</div>
              <div className="mb-1 mt-5 flex items-baseline gap-[6px]">
                <span className="num text-[44px] font-bold tracking-[-0.035em] text-vaulzo-ink">₹0</span>
                <span className="text-sm text-vaulzo-fainter">/ forever</span>
              </div>
              <div className="text-[13px] text-vaulzo-fainter">Manual tracking</div>
              <div className="my-6 h-px bg-[#EEEFF1]" />
              <div className="flex flex-1 flex-col gap-[13px]">
                {FREE_FEATURES.map((f) => (
                  <Feature key={f} stroke="#B7BABF" className="text-[#4B4E54]">
                    {f}
                  </Feature>
                ))}
              </div>
              <button className="mt-[26px] w-full rounded-[11px] bg-vaulzo-row px-4 py-[13px] text-[15px] font-semibold text-vaulzo-muted transition-colors hover:bg-[#E7E7E4]">
                Current plan
              </button>
            </div>

            {/* Yearly */}
            <div className="flex flex-col rounded-[20px] border border-[#E7E9EE] bg-white px-[26px] py-[30px]">
              <div className="text-[13px] font-bold uppercase tracking-[0.06em] text-vaulzo-faint">Yearly</div>
              <div className="mb-1 mt-5 flex items-baseline gap-[6px]">
                <span className="num text-[44px] font-bold tracking-[-0.035em] text-vaulzo-ink">₹399</span>
                <span className="text-sm text-vaulzo-fainter">/ year</span>
              </div>
              <div className="num text-[13px] text-vaulzo-fainter">just ₹33 / month</div>
              <div className="my-6 h-px bg-[#EEEFF1]" />
              <div className="flex flex-1 flex-col gap-[13px]">
                {YEARLY_FEATURES.map((f) => (
                  <Feature key={f} stroke="#5B6B8C" className="text-[#4B4E54]">
                    {f}
                  </Feature>
                ))}
              </div>
              <button className="mt-[26px] w-full rounded-[11px] bg-vaulzo-accent-tint px-4 py-[13px] text-[15px] font-semibold text-vaulzo-accent transition-colors hover:bg-[#E4E4F7]">
                Choose Yearly
              </button>
            </div>

            {/* Lifetime (highlighted) */}
            <div className="relative -mt-[18px]">
              <div className="rounded-[24px] border border-[#DDDEF4] bg-[#F3F4FB] pt-4 shadow-[0_26px_54px_rgba(23,24,26,0.16)]">
                <div className="px-0 pb-4 pt-[2px] text-center text-[12px] font-bold tracking-[0.04em] text-[#4A4C9C]">
                  Best value — pay once
                </div>
                <div className="flex flex-col rounded-[22px] bg-white px-7 py-[30px] shadow-[0_1px_2px_rgba(23,24,26,0.04)]">
                  <div className="flex items-center gap-[10px]">
                    <span className="text-[13px] font-bold uppercase tracking-[0.06em] text-vaulzo-ink">Lifetime</span>
                    <span className="rounded-full bg-vaulzo-accent px-[10px] py-[3px] text-[12px] font-bold text-white">Save 50%</span>
                  </div>
                  <div className="mb-1 mt-5 flex items-baseline gap-[6px]">
                    <span className="num text-[44px] font-bold tracking-[-0.035em] text-vaulzo-ink">₹999</span>
                    <span className="text-sm text-vaulzo-fainter">once</span>
                  </div>
                  <div className="num text-[13px] font-semibold text-[#3F8F6B]">Pays for itself in ~2.5 years</div>
                  <div className="my-6 h-px bg-[#EEEFF1]" />
                  <div className="flex flex-1 flex-col gap-[13px]">
                    {LIFETIME_FEATURES.map((f) => (
                      <Feature key={f} stroke="#5B6B8C" className="text-[#2E3036]">
                        {f}
                      </Feature>
                    ))}
                  </div>
                  <button className="mt-[26px] w-full rounded-[11px] bg-vaulzo-accent px-4 py-[14px] text-[15px] font-semibold text-white transition-colors hover:bg-vaulzo-accent-hover">
                    Get Lifetime
                  </button>
                  <div className="mt-[14px] text-center text-[12px] text-vaulzo-fainter">
                    30-day money-back guarantee
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center text-[12px] text-vaulzo-subtle">
            Cancel anytime · Prices shown at 50% launch discount
          </div>
        </div>
      </div>
    </div>
  );
}
